import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { ToastService } from '../../../@core/services/toast.service';
import { DialogService } from '../../../@core/services/dialog.service';
import { AdobeSignService } from '../../services/adobe-sign.service';
import { IntegrationsService } from '../../services/integrations.service';
import { SIGNATORY_TYPE } from '../../models/contract-management.model';
import { INTEGRATION_TYPE } from '../../models/integration-types.enum';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';

@Component({
  selector: 'app-adobe-sign',
  standalone: true,
  imports: [CommonModule, ButtonComponent, InputTextModule, ReactiveFormsModule, PasswordModule, AssetUrlPipe],
  templateUrl: './adobe-sign.component.html',
  styleUrl: './adobe-sign.component.scss'
})
export class AdobeSignComponent {
  @Output() close = new EventEmitter<{ connected?: boolean }>();

  adobeSignForm!: FormGroup;
  consentExists: boolean = false;
  connectLoading: boolean = false;
  revokeLoading: boolean = false;
  adobeSignSetting: any = {}
  dots = [].constructor(5);
  lastSynced: string = '';

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private toast: ToastService,
    private adobeSignService: AdobeSignService,
    private location: Location,
    private dialogService: DialogService,
    private integrationsService: IntegrationsService
  ) {

  }

  ngOnInit() {
    const code = this.activatedRoute.snapshot.queryParamMap.get('code')

    if (code) {
      this.setAdobesignOAuthToken(code)
    }

    this.adobeSignForm = this.fb.group({
      clientId: [''],
      clientSecret: ['']
    });
    this.getAdobeSignSettings();
    this.checkConsent();
  }

  getAdobeSignSettings() {
    this.adobeSignService.getSettings().subscribe((settings) => {
      this.adobeSignForm.get('clientId')?.setValue(settings.data?.clientId);
      this.adobeSignForm.get('clientSecret')?.setValue(settings.data?.clientSecret);
      this.lastSynced = settings.data ? moment(settings.data?.updatedAt).format('lll') : ""
    })
  }

  setAdobesignOAuthToken(code: string) {
    this.adobeSignService.setAccessToken(code, location.href).subscribe((res) => {
      if (res.success) {
        if (!res.data) {
          this.location.replaceState('/settings/integrations');
          return;
        }
        this.consentExists = res.data.consent
        this.toast.success('Adobe Sign consent granted!')
        this.location.replaceState('/settings/integrations');
        this.close.emit({ connected: true });
      }
    })
  }

  saveAdobeSignSettings(showToast = true) {
    return new Promise((resolve, reject) => {
      const credentials = this.adobeSignForm.value;
      this.adobeSignService.saveSettings(credentials).subscribe((res) => {
        if (res.success) {
          if (showToast) this.toast.success('Adobe Sign Credentials Saved Successfully!')
          resolve(true)
        } else {
          reject(false)
        }
      })
    })
  }

  async checkIfAnotherSignatoryConnected(): Promise<{ isConnected: boolean; signatoryName: string }> {
    return new Promise((resolve) => {
      this.integrationsService.getAllIntegrations().subscribe((res: any) => {
        if (res.success) {
          const integrations = Array.isArray(res.data)
            ? res.data
            : (res.data?.integrations || res.data || []);

          const signatoryIntegration = integrations.find(
            (integration: any) => integration.type === INTEGRATION_TYPE.SIGNATORY_SETTINGS
          );

          if (signatoryIntegration && signatoryIntegration.values && Array.isArray(signatoryIntegration.values) && signatoryIntegration.values.length > 0) {
            const connectedValues = signatoryIntegration.values.map((v: any) => String(v));
            // Check if DocuSign is connected (not Adobe Sign)
            if (connectedValues.includes(String(SIGNATORY_TYPE.DOCU_SIGN))) {
              resolve({ isConnected: true, signatoryName: 'DocuSign' });
              return;
            }
          }
        }
        resolve({ isConnected: false, signatoryName: '' });
      });
    });
  }

  async getConsentCode() {
    // Check if another signatory is already connected
    const { isConnected, signatoryName } = await this.checkIfAnotherSignatoryConnected();

    if (isConnected) {
      this.dialogService
        .confirm({
          title: 'Confirm Signatory Change',
          description: `Previous signatory (${signatoryName}) consent will be revoked. Do you want to proceed?`,
          confirmText: 'Proceed',
          cancelText: 'Cancel',
        })
        .subscribe((confirmed) => {
          if (confirmed) {
            this.proceedWithConsent();
          }
        });
    } else {
      // No other signatory connected, proceed directly
      this.proceedWithConsent();
    }
  }

  private async proceedWithConsent() {
    this.connectLoading = true;
    const saveAdobeSettings = await this.saveAdobeSignSettings(false)
    if (!saveAdobeSettings) {
      this.toast.error('Adobe Sign Credentials not saved successfully!')
      this.connectLoading = false;
      return;
    }
    this.adobeSignService.authAdobeSignature().subscribe({
      next: (response) => {
        if (response.success) {
          const result = response.data
          if (result.data) {
            this.adobeSignSetting.consent = true
          } else {
            const _payload = response.data.payload
            const url = new URL(_payload.url)
            url.searchParams.append('response_type', _payload.response_type)
            url.searchParams.append('client_id', _payload.client_id)
            url.searchParams.append('redirect_uri', location.href + '/adobe-sign')
            url.searchParams.append('scope', _payload.scope)
            location.href = url.toString()
          }
        }
        this.connectLoading = false;
      },
      error: () => {
        this.connectLoading = false;
      }
    })
  }

  revokeConsent() {
    this.adobeSignService.revokeConsent().subscribe((response) => {
      if (response.success) {
        this.toast.success('Adobe Consent Revoked!')
        this.consentExists = false
        this.getAdobeSignSettings();
        this.revokeLoading = false;
        this.close.emit({ connected: false });
      }
    })
  }

  checkConsent() {
    this.adobeSignService.authAdobeSignature().subscribe((response) => {
      const result = response.data
      if (result.exists) {
        this.consentExists = true
      }
    })
  }
}
